import { Box, Link, styled } from '@mui/material';
import { Link as RouterLink } from 'react-router';

import { routes } from '@/assets/constants/routes-no.ts';
import { SEO } from '@/lib/seo.tsx';
import useBrainSession from '@/services/BrainSession/useBrainSession.ts';

import { SIZES } from '../../assets/constants/constants.ts';
import ActionButton from '../../components/ActionButton/index.ts';
import Circle from '../../components/Circle/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_COMPARE_CONSUMER_LOANS } from './RRS.ts';

const StyledCircleLeft = styled(Circle)({
  left: -130,
  top: -180,
});

const StyledCircleRight = styled(Circle)({
  right: -190,
  top: 160,
});

const ContentBox = styled(Box)(({ theme }) => ({
  maxWidth: SIZES.layout.width.sm,
  boxShadow: theme.shadows[21],
  background: theme.palette.background.paper,
  display: 'flex',
  textAlign: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 8,
  width: '100%',
  padding: theme.spacing(5, 3),
  color: theme.palette.text.primary,
  margin: theme.spacing(4, 0),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(9, 5),
  },
}));

const StyledActionButton = styled(ActionButton)(({ theme }) => ({
  maxWidth: 550,
  [theme.breakpoints.up('sm')]: {
    minWidth: 330,
  },
}));

const ArticleSectionsContainer = styled(Box)(({ theme }) => ({
  maxWidth: SIZES.layout.width.sm,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
}));

const TopSection = styled(Box)(({ theme }) => ({
  maxWidth: SIZES.layout.width.sm,
  display: 'flex',
  flexDirection: 'column',
  '& > button': {
    alignSelf: 'center',
  },
  '& a': {
    ...theme.typography.subtitle2,
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  ...theme.typography.p3,
})) as typeof Link;

const StyledList = styled('ul')(({ theme }) => ({
  ...theme.typography.p3,
  paddingLeft: theme.spacing(3.5),
  '& > li': {
    marginBottom: theme.spacing(1.5),
  },
}));

const CompareConsumerLoans: React.FC = () => {
  const { toggleChat } = useBrainSession();

  return (
    <>
      <SEO
        title="Sammenlign forbrukslån fra 13 banker"
        description="Én søknad, flere tilbud. Sammenlign effektiv rente, gebyrer og månedskostnad før du velger. Gratis og uforpliktende."
        path={routes.COMPARE_CONSUMER_LOANS}
      />
      <RrsHandler {...RRS_COMPARE_CONSUMER_LOANS} />
      <Section fullWidth background="primary">
        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <StyledCircleLeft size={380} border={80} color="lightTransparent" />
          <StyledCircleRight size={380} border={80} color="lightTransparent" />
        </Box>

        <ContentBox>
          <h1 className="typography-v1-legacy-h2">Sjekk forbrukslånet på 1 minutt</h1>
          <p className="mt-6 mb-10 max-w-105 typography-v1-legacy-p1">
            Gratis tjeneste som automatisk sjekker renten raskt og enkelt.
          </p>
          <StyledActionButton
            label="Sjekk nå"
            flow="debt_check"
            step="refin_present_terms_auto_new"
          />
        </ContentBox>
      </Section>
      <Section fullWidth>
        <TopSection>
          <h1 className="mb-8 typography-v1-legacy-h2 text-v1-primary-100">
            Sammenligne forbrukslån? Så enkelt sparer du tid og penger!
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Når du skal søke om forbrukslån bruker du mindre tid om du sender én søknad til
            sammenligningstjenesten Scoopr. Og siden vi sender søknaden din videre til 13 banker, er
            sannsynligheten stor for at du sparer penger også. Mye penger.
          </h2>
          <p className="typography-v1-legacy-subtitle2">
            Med Scoopr kan du nemlig trygt og enkelt sammenligne forbrukslån og velge den banken som
            gir deg deg beste tilbudet, altså lavest rente. Om du ønsker, kan du selvsagt ta all
            jobben med å søke og sammenligne på egenhånd.
            <br />
            <br />
            Hvis du velger å sammenligne forbrukslån selv må du være obs på at det kan være noen
            fallgruver du kan ramle ned i – og som kan være vanskelige å komme seg opp av. Du kan
            med andre ord gå glipp av lavere rente og bedre betingelser enn om du lar Scoopr gjøre
            jobben for deg.
          </p>
        </TopSection>
      </Section>
      <Section fullWidth background="paper" last>
        <ArticleSectionsContainer>
          <Box
            sx={{
              pr: { xs: 0, sm: 3 },
              width: { xs: '100%', sm: '50%' },
            }}
          >
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Sammenligne forbrukslån? Dette må du vite
            </h3>
            <p className="typography-v1-legacy-p3">
              Når du sammenligner forbrukslån hos én og én bank kan du fort miste oversikten, og du
              vil bruke mye tid på å lete, søke og finne ut av hva du faktisk må betale per måned.
              Om du likevel ønsker å gjøre det på denne måten, må du kjenne til følgende:
              <br />
              <br />
              <br />
            </p>

            <h4 className="mb-4 typography-v1-legacy-p1SemiBold">Effektiv og nominell rente</h4>
            <p className="typography-v1-legacy-p3">Kort forklart er dette forskjellen:</p>
            <StyledList>
              <li>
                Den nominelle renten er grunnrenten på lånet ditt – uten gebyrer, avgifter og andre
                kostnader. Denne er alltid lavere enn den effektive renten.
              </li>
              <li>
                Den effektive renten er grunnrenten på lånet ditt – pluss gebyrer, avgifter og andre
                kostnader. Denne er alltid høyere enn den nominelle renten.
              </li>
            </StyledList>
            <p className="typography-v1-legacy-p3">
              Når du skal sammenligne forbrukslån må du derfor alltid vite hva den effektive renten
              er, da det er den som faktisk viser hvor mye du skal betale på lånet ditt.
              <br />
              <br />
              Prøv forbrukslånskalkulatoren vår – da ser du enkelt hva lånet koster deg.
              <br />
              <br />
              <br />
            </p>

            <h4 className="mb-4 typography-v1-legacy-p1SemiBold">Hvilken rente får du?</h4>
            <p className="typography-v1-legacy-p3">
              På nettsidene til forbrukslånsbanker står det alltid en såkalt disclaimer, som viser
              renten, lånebeløp, kostnad og hva du totalt må betale.
            </p>
            <StyledList>
              <li>Renten du ser, viser kun gjennomsnittet av bankens innvilgede lån.</li>
              <li>Renten du får, avhenger av beløpet du søker om og privatøkonomien din.</li>
            </StyledList>
            <p className="mb-14 typography-v1-legacy-p3">
              Når bankene får søknaden din, foretar de en kredittsjekk basert på blant annet
              inntekten, formuen, gjelden og betalingshistorikken din. Jo bedre score du får på
              kredittsjekken, desto lavere rente får du på forbrukslånet ditt.
              <br />
              <br />
              <strong>
                Du kan{' '}
                <StyledLink
                  component={RouterLink}
                  to={routes.CREDIT_CHECK_MYSELF}
                  underline="hover"
                >
                  lese mer om hvordan du kredittsjekker deg selv
                </StyledLink>{' '}
                – eller{' '}
                <StyledLink
                  component="button"
                  onClick={() =>
                    toggleChat({
                      goal: 'debt_check',
                      step: 'refin_present_terms_auto_new',
                    })
                  }
                  underline="hover"
                >
                  gjøre det her.
                </StyledLink>
              </strong>
            </p>
          </Box>

          <Box
            sx={{
              pl: { xs: 0, sm: 3 },
              width: { xs: '100%', sm: '50%' },
            }}
          >
            <h4 className="mb-4 typography-v1-legacy-p1SemiBold">
              Hvor mye kan du betale per måned?
            </h4>
            <p className="mb-14 typography-v1-legacy-p3">
              Her gjelder det å være varsom, og ikke makse lånebeløpet ditt. Lag et ordentlig
              budsjett, og sørg for at du både kan leve godt og har rom for uforutsette utgifter –
              for de kommer alltid når du minst venter det.
              <br />
              <br />
              Tenk også over om du vil ha{' '}
              <StyledLink component={RouterLink} to={routes.REPAYMENT_PERIOD} underline="hover">
                kort eller lang nedbetalingstid
              </StyledLink>
              . Jo kortere den er desto mindre betaler du, og nettopp derfor bør du bruke tid på å
              regne deg frem til hva som er riktig beløp for økonomien din. Og som nevnt – riktig
              beløp er ikke å låne så mye som mulig.
              <br />
              <br />
              Det er viktig at du følger nedbetalingsplanen slik at lånet ikke blir misligholdt, noe
              som igjen kan føre til betalingsanmerkninger. Da får du ikke nye lån før anmerkningen
              er fjernet.
              <br />
              <br />
              <strong>
                Som du ser er det mye å tenke over når du skal gjøre jobben selv. Heldigvis finnes
                det en enklere måte å sammenligne forbrukslån på.
              </strong>
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              La Scoopr sammenligne forbrukslån for deg
            </h3>
            <p className="typography-v1-legacy-p3">Det gjør at du sparer mye:</p>

            <StyledList>
              <li>
                Tid – fordi du må surfe rundt etter det beste tilbudet, som du kanskje ikke engang
                finner siden det er mange banker som tilbyr forbrukslån.
              </li>
              <li>Penger – fordi vi har funnet de bankene vi vet vil gi deg det beste tilbudet.</li>
            </StyledList>

            <p className="mb-14 typography-v1-legacy-p3">
              Hos Scoopr er det uforpliktende, enkelt og gratis å sammenligne forbrukslån. Du kan
              søke om lån fra 10.000 til 600.000 kroner, og du gjør det på to minutter:
              <br />
              <br />
              <strong>
                Ha BankID klar,{' '}
                <StyledLink
                  component="button"
                  onClick={() =>
                    toggleChat({
                      goal: 'debt_check',
                      step: 'refin_present_terms_auto_new',
                    })
                  }
                  underline="hover"
                >
                  trykk her
                </StyledLink>
                , fyll inn opplysningene dine – og så er jobben gjort!
              </strong>
              <br />
              <br />
              Nå går søknaden din automatisk til 13 banker, som alle ønsker å gi deg så lav rente
              som mulig. I løpet av kort tid mottar du tilbudene, og da har du hele 30 dager på deg
              til å vurdere hvilket som er best for deg. Når du har valgt banken din, får du som
              regel pengene inn på konto etter et par dager.
              <br />
              <br />
              <strong>
                Vil du at Scoopr sammenligner forbrukslån for deg?{' '}
                <StyledLink
                  component="button"
                  onClick={() =>
                    toggleChat({
                      goal: 'debt_check',
                      step: 'refin_present_terms_auto_new',
                    })
                  }
                  underline="hover"
                >
                  Da søker du her.
                </StyledLink>
              </strong>
            </p>
          </Box>
        </ArticleSectionsContainer>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <StyledActionButton
            label="Sjekk nå"
            flow="debt_check"
            step="refin_present_terms_auto_new"
          />
          <Disclaimer />
        </Box>
      </Section>
    </>
  );
};

export default CompareConsumerLoans;
